import { Serializable } from "@/core/api/decorators/serializable_decorator"
import {
  ContractBaseData,
  ContractBaseModel,
} from "@/modules/contract/models/contractBaseModel"
import { SubAreaModel } from "@/modules/prices/models/subAreaModel"
import { UserModel } from "@/modules/users/models/userModel"
import { HiringLocation } from "@/modules/tasks/models/rfs/hiringLocation"

import { ConsultantUnavailableAction } from "../constants/consultantUnavailableAction"
import { ExtensionStatus } from "../constants/extensionStatus"
import { B2BExtensionData, B2BExtensionModel } from "./b2bExtensionModel"
import { ExtensionConfirmationEmailModel } from "./extensionConfirmationEmailModel"
import { ExtensionOfferModel } from "./extensionOfferModel"

export interface ExtensionRequestData
  extends ContractBaseData<ExtensionStatus> {
  priceValue?: number
  priceValueCurrency?: string
  localPriceValue?: number
  localPriceValueCurrency?: string
  maxHoursInContract?: number
  created?: string
  justification?: string
  cancelMessage?: string
  consultantUnavailableAction?: ConsultantUnavailableAction
  confirmationEmail?: ExtensionConfirmationEmailModel | null
  offers?: ExtensionOfferModel[]
  subArea?: SubAreaModel | null
  selectedLocation?: HiringLocation | null
  b2bExtension?: B2BExtensionData | null
  contractId?: string
  extensionNumber?: number
  procurementContact?: UserModel | null
}

@Serializable
export class ExtensionRequestModel extends ContractBaseModel<ExtensionStatus> {
  priceValue: number = 0
  priceValueCurrency: string = ""
  localPriceValue: number = 0
  localPriceValueCurrency: string = ""
  maxHoursInContract: number = 0
  status: ExtensionStatus = ExtensionStatus.NotDefined
  created: string = ""
  justification: string = ""
  cancelMessage: string = ""
  consultantUnavailableAction: ConsultantUnavailableAction =
    ConsultantUnavailableAction.AllowReplacement
  confirmationEmail: ExtensionConfirmationEmailModel | null = null
  offers: ExtensionOfferModel[] = []
  subArea: SubAreaModel | null = null
  extensionNumber: number = 0
  selectedLocation: HiringLocation | null = null
  b2bExtension: B2BExtensionModel | null = null
  contractId: string = ""
  /** Procurement-side contact on the request, or null when none is set. */
  procurementContact: UserModel | null = null

  constructor(data: ExtensionRequestData | null = null) {
    super(data)
    if (data) {
      this.priceValue = data.priceValue ?? 0
      this.priceValueCurrency = data.priceValueCurrency ?? ""
      this.localPriceValue = data.localPriceValue ?? 0
      this.localPriceValueCurrency = data.localPriceValueCurrency ?? ""
      this.maxHoursInContract = data.maxHoursInContract ?? 0
      this.status = data.status ?? ExtensionStatus.NotDefined
      this.created = data.created ?? ""
      this.justification = data.justification ?? ""
      this.cancelMessage = data.cancelMessage ?? ""
      this.consultantUnavailableAction =
        data.consultantUnavailableAction ??
        ConsultantUnavailableAction.AllowReplacement
      this.confirmationEmail = data.confirmationEmail
        ? new ExtensionConfirmationEmailModel(data.confirmationEmail)
        : null
      this.offers =
        data.offers?.map((offer) => new ExtensionOfferModel(offer)) ?? []
      this.subArea = data.subArea ? new SubAreaModel(data.subArea) : null
      this.selectedLocation = data.selectedLocation
        ? new HiringLocation(data.selectedLocation)
        : null
      this.b2bExtension = data.b2bExtension
        ? new B2BExtensionModel(data.b2bExtension)
        : null
      this.contractId = data.contractId ?? ""
      this.extensionNumber = data.extensionNumber ?? 0
      this.procurementContact = data.procurementContact
        ? new UserModel(data.procurementContact)
        : null
    }
  }
}
